/**
 * Bulk grid: when a growth row links a precompounded starting material, preparation recipes
 * are owned by the precursor sample row — not the growth crystal row (match Knockout precompoundedLabel only).
 */

import { isAuthorized as generalIsAuthorized } from '@/api/AuthHelper';
import { PrivilegeEnum, StatusDto, type RoleWithPrivilegesDto } from '@/api/ApiClient';
import type { Operation } from '@/stores/authStore';

/** True when viewing a precursor's preparation recipe from a different bulk row (growth sample). */
export function isPreparationRecipeViewedFromGrowthRow(
  viewingSampleLabel: string | undefined,
  precompoundedLabel: string | undefined
): boolean {
  if (!precompoundedLabel) {
    return false;
  }
  if (!viewingSampleLabel) {
    return true;
  }
  return viewingSampleLabel !== precompoundedLabel;
}

export type BulkProcessingAuthContext = {
  isAuthorized: (privilege: PrivilegeEnum | null | undefined, op: Operation) => boolean;
  bulkRolePrivileges: readonly RoleWithPrivilegesDto[];
};

/** JWT ProcessingActivity update, or project collaborator role from bulk grid load. */
export function canUpdateBulkProcessingActivity(
  ctx: BulkProcessingAuthContext,
  ownerRowRoles?: readonly number[] | null
): boolean {
  if (ctx.isAuthorized(PrivilegeEnum.ProcessingActivity, 'update')) {
    return true;
  }
  const roleIds = ownerRowRoles ?? [];
  return roleIds.some((roleId) => {
    const role = ctx.bulkRolePrivileges.find((r) => r.id === roleId);
    return role?.privileges ? generalIsAuthorized(role.privileges, PrivilegeEnum.ProcessingActivity, 'update') : false;
  });
}

/** Whether the preparation recipe could be edited on the owner sample's bulk row (status + privilege). */
export function canEditPreparationRecipe(
  activityStatus: StatusDto | undefined,
  auth: BulkProcessingAuthContext,
  ownerRowRoles?: readonly number[] | null
): boolean {
  if (activityStatus !== StatusDto.Pending && activityStatus !== StatusDto.New) {
    return false;
  }
  return canUpdateBulkProcessingActivity(auth, ownerRowRoles);
}

/** User-facing hint when preparation must be edited from the precursor's own bulk row. */
export function getCrossRowPreparationRecipeMessage(
  viewingSampleLabel: string | undefined,
  preparationOwnerLabel: string | undefined,
  startingMaterialNumber?: number
): string {
  const owner = preparationOwnerLabel;
  const smNote = startingMaterialNumber != null ? ` (starting material ${startingMaterialNumber})` : '';

  if (viewingSampleLabel && owner) {
    return `Sample ${viewingSampleLabel} uses ${owner}${smNote} as a precompounded starting material. This preparation recipe belongs to ${owner} — edit it from ${owner}'s bulk row.`;
  }
  if (owner) {
    return `This preparation recipe belongs to sample ${owner}${smNote}. Edit it from that sample's bulk row.`;
  }
  return `This preparation recipe must be edited from the starting material sample's own bulk row.${smNote}`;
}

/** View-mode status line: short when edit is impossible; cross-row directions only when edit is possible elsewhere. */
export function getPreparationRecipeViewHint(params: {
  hasRecipe: boolean;
  forceReadOnly?: boolean;
  viewingSampleLabel?: string;
  preparationOwnerLabel?: string;
  startingMaterialNumber?: number;
  activityStatus?: StatusDto;
  canEditOnOwnerRow: boolean;
}): string | null {
  if (!params.hasRecipe) {
    return null;
  }

  if (params.activityStatus === StatusDto.Submitted) {
    return 'Recipe cannot be edited (sample has been submitted)';
  }

  if (!params.canEditOnOwnerRow) {
    return params.forceReadOnly ? 'View only.' : 'Recipe cannot be edited';
  }

  if (params.forceReadOnly) {
    return getCrossRowPreparationRecipeMessage(
      params.viewingSampleLabel,
      params.preparationOwnerLabel,
      params.startingMaterialNumber
    );
  }

  return null;
}
